import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, FolderOpen, Receipt, BarChart3, Users, Building2, Shield } from 'lucide-react';

interface FaqItem {
  question: string;
  answer: string;
  icon: React.ReactNode;
}

const FAQ_SECTIONS: { title: string; items: FaqItem[] }[] = [
  {
    title: 'Primeros pasos',
    items: [
      {
        question: '¿Cómo inicio sesión por primera vez?',
        answer: 'Al ser dado de alta, recibirás un correo con tus credenciales temporales. Ingresa con ellas y el sistema te pedirá establecer una nueva contraseña antes de continuar.',
        icon: <Shield className="h-5 w-5" />,
      },
      {
        question: '¿Cómo cambio mi contraseña?',
        answer: 'Ve a tu perfil haciendo click en tu avatar en la esquina superior derecha, luego selecciona "Mi Perfil". Ahí encontrarás la sección "Cambiar Contraseña".',
        icon: <Shield className="h-5 w-5" />,
      },
    ],
  },
  {
    title: 'Proyectos',
    items: [
      {
        question: '¿Cómo creo un proyecto?',
        answer: 'Ve al menú "Proyectos" y haz click en "Nuevo Proyecto". Completa el nombre, presupuesto y fechas. Solo los administradores pueden crear proyectos.',
        icon: <FolderOpen className="h-5 w-5" />,
      },
      {
        question: '¿Qué significan los estados de un proyecto?',
        answer: 'Los proyectos pueden estar en estado Planificación, En Progreso, Pausado o Completado. El estado ayuda a organizar y filtrar tus proyectos activos.',
        icon: <FolderOpen className="h-5 w-5" />,
      },
    ],
  },
  {
    title: 'Gastos',
    items: [
      {
        question: '¿Cómo registro un gasto?',
        answer: 'Ve al menú "Gastos" y haz click en "Nuevo Gasto". Debes seleccionar un proyecto, categoría, monto y fecha. Puedes adjuntar comprobantes en formato PDF, JPG o PNG.',
        icon: <Receipt className="h-5 w-5" />,
      },
      {
        question: '¿Por qué no puedo crear gastos?',
        answer: 'Para registrar gastos necesitas tener al menos un proyecto creado. Pide a tu administrador que cree un proyecto primero.',
        icon: <Receipt className="h-5 w-5" />,
      },
      {
        question: '¿Puedo editar un gasto ya creado?',
        answer: 'Sí, puedes editar tus propios gastos. Los administradores pueden editar cualquier gasto de la empresa.',
        icon: <Receipt className="h-5 w-5" />,
      },
    ],
  },
  {
    title: 'Reportes',
    items: [
      {
        question: '¿Qué tipos de reportes hay?',
        answer: 'Hay tres tipos: por Categoría (agrupa gastos por tipo), por Tiempo (muestra tendencias semanales, quincenales o mensuales) y por Proyecto (compara presupuesto vs gasto real).',
        icon: <BarChart3 className="h-5 w-5" />,
      },
      {
        question: '¿Puedo exportar los reportes?',
        answer: 'Sí, los reportes se pueden exportar en formato Excel y PDF desde la vista de reportes.',
        icon: <BarChart3 className="h-5 w-5" />,
      },
    ],
  },
  {
    title: 'Usuarios y permisos',
    items: [
      {
        question: '¿Qué roles existen?',
        answer: 'Hay dos roles principales: Administrador (gestiona proyectos, usuarios, categorías y ve todos los gastos) y Colaborador (registra gastos en los proyectos asignados y ve reportes).',
        icon: <Users className="h-5 w-5" />,
      },
      {
        question: '¿Cómo agrego usuarios a mi empresa?',
        answer: 'Como administrador, ve al menú "Usuarios" y haz click en "Nuevo Usuario". Ingresa el correo, nombre y rol. El usuario recibirá un correo con sus credenciales de acceso.',
        icon: <Users className="h-5 w-5" />,
      },
    ],
  },
  {
    title: 'Mi empresa y plan',
    items: [
      {
        question: '¿Cómo veo los detalles de mi plan?',
        answer: 'Ve al menú "Mi Empresa" para ver tu plan actual, uso de usuarios y gastos mensuales. Ahí puedes ver cuánto has consumido de tus límites.',
        icon: <Building2 className="h-5 w-5" />,
      },
      {
        question: '¿Qué pasa si alcanzo el límite de usuarios?',
        answer: 'No podrás crear nuevos usuarios hasta que se desactiven algunos existentes o se actualice tu plan a uno superior. Los usuarios existentes no se ven afectados.',
        icon: <Building2 className="h-5 w-5" />,
      },
    ],
  },
];

const FaqAccordion: React.FC<{ item: FaqItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-primary-500 flex-shrink-0">{item.icon}</span>
        <span className="flex-1 text-sm font-medium text-gray-900">{item.question}</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-3 pl-12">
          <p className="text-sm text-gray-600 leading-relaxed">{item.answer}</p>
        </div>
      )}
    </div>
  );
};

const Help: React.FC = () => {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <HelpCircle className="h-8 w-8 text-primary-500" />
          <h1 className="text-3xl font-bold text-gray-900">Centro de Ayuda</h1>
        </div>
        <p className="text-gray-600">Respuestas a las preguntas más frecuentes sobre el uso de Kessan.</p>
      </div>

      <div className="space-y-8">
        {FAQ_SECTIONS.map((section) => (
          <div key={section.title}>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">{section.title}</h2>
            <div className="space-y-2">
              {section.items.map((item) => (
                <FaqAccordion key={item.question} item={item} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 bg-blue-50 border border-blue-200 rounded-lg p-5 text-center">
        <p className="text-sm text-blue-800">
          ¿No encontraste lo que buscabas? Contacta al administrador de tu empresa para obtener ayuda adicional.
        </p>
      </div>
    </div>
  );
};

export default Help;
